import React, { useCallback } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { StyleSheet } from 'react-native';

import { theme } from '@garnish/constants';
import { useResponsive } from '@garnish/core';

import { Address, useAddressHandlers as useAddressHandlersV2 } from '@order/features-locations';

import { useCustomer } from '../../context/Customer';
import { useCart } from '../../hooks';
import { HorizontalSafeAreaContainer } from '../HorizontalSafeAreaContainer';
import { Modal } from '../Modal';

export const AddressEditModal = (props: AddressEditModalProps) => {
  const { addressId, visible, onRequestClose } = props;

  // ─────────────────────────────────────────────────────────────────────────────

  const { formatMessage } = useIntl();
  const { currentBreakpoint, match } = useResponsive();
  const { customer } = useCustomer();
  const { cart, isCartEmpty, fetchCart } = useCart();

  const customerAddresses = customer?.addresses ?? [];
  const address = customerAddresses?.find((item) => item.id === addressId);

  const currentBagAddressId = cart?.deliveryOrderDetail?.address?.id;
  const isEditingBagAddress = currentBagAddressId === addressId;
  const shouldUpdateCart = !isCartEmpty && isEditingBagAddress;

  // ─── Callbacks ───────────────────────────────────────────────────────────────────

  const handleOnSuccess = useCallback(() => {
    if (shouldUpdateCart) {
      fetchCart();
    }

    onRequestClose();
  }, [fetchCart, onRequestClose, shouldUpdateCart]);

  // ─────────────────────────────────────────────────────────────────────────────

  const {
    handleOnEdit: handleOnEditV2,
    handleOnDelete: handleOnDeleteV2,
    isUpdatingAddress: isUpdatingAddressV2,
    isDeletingAddress: isDeletingAddressV2,
  } = useAddressHandlersV2({
    onSuccess: handleOnSuccess,
    editingAddressId: addressId,
  });

  return (
    <Modal
      fitHeight
      visible={visible}
      accessibilityLabel={formatMessage(messages.modalHeadline)}
      presentationStyle={currentBreakpoint.isXS ? 'pageSheet' : undefined}
      size={match(['full', 'small'])}
      containerStyle={match([undefined, styles.containerSM])}
      withSafeAreaView={false}
      onRequestClose={onRequestClose}
    >
      <HorizontalSafeAreaContainer>
        <Modal.Header onClose={onRequestClose}>
          <FormattedMessage {...messages.modalHeadline} />
        </Modal.Header>

        <Address.Form
          visible
          editing
          savedCustomerAddress={address}
          customerAddresses={customerAddresses}
          editingAddressId={addressId}
          onSubmit={handleOnEditV2}
          isLoading={isUpdatingAddressV2}
          onDelete={handleOnDeleteV2}
          isDeleting={isDeletingAddressV2}
        />
      </HorizontalSafeAreaContainer>
    </Modal>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  containerSM: {
    paddingTop: theme.spacing['6'],
    paddingBottom: theme.spacing['6'],
  },
});

// ─── Messages ───────────────────────────────────────────────────────────────────

const messages = defineMessages({
  saveAddress: {
    defaultMessage: 'Save',
    description: 'Account | Edit Address Form | Save',
  },
  modalHeadline: {
    defaultMessage: 'Edit Address',
    description: 'Account | Modal header | Edit Address',
  },
});

// ─── Types ─────────────────────────────────────────────────────────────

type AddressEditModalProps = Readonly<{
  addressId: string;
  visible: boolean;
  onRequestClose: () => void;
}>;
